import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

/**
 * Shown in-place on the Login page after a valid password when the user has
 * TOTP enabled. Submits either a 6-digit TOTP or a "XXXXX-XXXXX" backup code
 * plus an optional "trust this device for 30 days" flag.
 */
export default function TwoFactorPrompt({ challengeToken, onSuccess, onCancel }) {
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/login", {
        challenge_token: challengeToken,
        code: code.trim(),
        trust_device: trustDevice,
      });
      toast.success(`Welcome back, ${data.name.split(" ")[0]}`);
      onSuccess(data);
    } catch (err) {
      toast.error("Verification failed", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="tfa-prompt">
      <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="text-sm text-slate-700">
          <div className="font-semibold text-slate-900">One more step</div>
          <div className="text-slate-600 mt-0.5">
            Open your authenticator app and enter the 6-digit code for Audentra.
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="label-caps">{useBackup ? "Backup code" : "Authenticator code"}</Label>
        <Input
          value={code}
          onChange={(e) => setCode(useBackup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))}
          autoFocus
          maxLength={useBackup ? 32 : 6}
          inputMode={useBackup ? "text" : "numeric"}
          autoComplete="one-time-code"
          placeholder={useBackup ? "XXXXX-XXXXX" : "123456"}
          data-testid="tfa-prompt-code"
          className="font-mono tracking-widest text-center text-lg h-12"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="tfa-prompt-trust-label">
        <Checkbox
          checked={trustDevice}
          onCheckedChange={(v) => setTrustDevice(!!v)}
          data-testid="tfa-prompt-trust"
        />
        Trust this device for 30 days
      </label>

      <Button
        type="submit"
        disabled={busy || !code.trim()}
        data-testid="tfa-prompt-submit"
        className="w-full h-11 bg-teal-700 hover:bg-teal-800 rounded-full"
      >
        {busy ? "Verifying…" : "Verify & sign in"}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => { setUseBackup((v) => !v); setCode(""); }}
          className="text-teal-700 hover:text-teal-800 font-semibold inline-flex items-center gap-1"
          data-testid="tfa-prompt-toggle-backup"
        >
          <KeyRound className="h-3.5 w-3.5" />
          {useBackup ? "Use authenticator code" : "Use a backup code"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-500 hover:text-slate-700"
          data-testid="tfa-prompt-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
